import { useEffect, useState } from 'react'
import { ExternalLink, Heart, MessageCircle, Search, User } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'
import { estErreurApi, fraicheurFr, nombreFr } from './lib.js'
import type { CommentRow } from './InstagramData.js'

/**
 * COMMENTAIRES (V1.3) — une CONVERSATION premium, pas un DataGrid.
 *
 * Tout vient de PostgreSQL (le worker synchronise) — Meta n'est jamais
 * appelé au rendu. Aucun avatar inventé : Meta ne fournit pas la photo dans
 * ce flow, on affiche l'initiale (§21, §61). Le username ouvre Instagram
 * dans un nouvel onglet — navigation utilisateur, jamais de crawl (§82).
 */

interface Thread { comment: CommentRow; replies: CommentRow[] }
interface Reponse {
  threads: Thread[]
  total: number
  syncedTotal: number
  provider_count: number | null
  synced_at: string | null
  support: { state: string; detail: string | null } | null
}

function Avatar({ username }: { username: string | null }) {
  if (!username) return <span className="igc-avatar" aria-hidden="true"><User size={14} /></span>
  return <span className="igc-avatar" aria-hidden="true">{username.slice(0, 1).toUpperCase()}</span>
}

function heureCourte(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

function Commentaire({ c, reponse = false }: { c: CommentRow; reponse?: boolean }) {
  return (
    <div className={`igc-item ${reponse ? 'igc-item--reponse' : ''} ${c.missing_since ? 'igc-item--disparu' : ''}`}>
      <Avatar username={c.author_username} />
      <div className="igc-corps">
        <div className="igc-tete">
          {c.author_username ? (
            <a href={`https://www.instagram.com/${encodeURIComponent(c.author_username)}/`}
              target="_blank" rel="noreferrer" className="igc-user">
              @{c.author_username} <ExternalLink size={11} aria-hidden="true" />
            </a>
          ) : <span className="igc-user igc-user--anon">Compte inconnu</span>}
          <span className="igc-quand">{heureCourte(c.provider_timestamp)}</span>
          {/* like_count : affiché UNIQUEMENT si Meta le renvoie — jamais un
              zéro inventé (§23). */}
          {typeof c.like_count === 'number' && c.like_count > 0 ? (
            <span className="igc-likes"><Heart size={11} aria-hidden="true" /> {nombreFr(c.like_count)}</span>
          ) : null}
          {c.missing_since ? <span className="lc-pill lc-pill--idle igc-pill">N’apparaît plus chez Instagram</span> : null}
        </div>
        <p className="igc-texte">{c.text || <em className="lc-hint">…</em>}</p>
      </div>
    </div>
  )
}

export function SectionCommentaires({ contentId }: { contentId: string }) {
  const [data, setData] = useState<Reponse | null>(null)
  const [threads, setThreads] = useState<Thread[]>([])
  const [page, setPage] = useState(0)
  const [recherche, setRecherche] = useState('')
  const [rechercheActive, setRechercheActive] = useState('')
  const [deploye, setDeploye] = useState<Set<string>>(new Set())
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => { setPage(0); setThreads([]) }, [rechercheActive, contentId])
  useEffect(() => {
    const qp = new URLSearchParams({ page: String(page) })
    if (rechercheActive) qp.set('search', rechercheActive)
    void apiGet<Reponse | { error: string; message?: string }>(
      `/api/v1/communication/instagram/contenus/${contentId}/commentaires?${qp}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lecture impossible.'); return }
        setErreur(null); setData(j)
        setThreads((prev) => (page === 0 ? j.threads : [...prev, ...j.threads]))
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [contentId, page, rechercheActive])

  const permissionManquante = data?.support?.state === 'UNAVAILABLE'
    && (data.support.detail ?? '').includes('Reconnectez')

  return (
    <section className="lc-panel">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">
          Commentaires{data ? <span className="igc-count"> {data.syncedTotal}</span> : null}
        </h2>
        <span className="lc-hint">
          {data?.provider_count !== null && data !== null && data.provider_count !== data.syncedTotal
            ? `${data.syncedTotal} chargés sur ${data.provider_count} · `
            : null}
          {data?.synced_at ? `synchronisé ${fraicheurFr(data.synced_at)}` : null}
        </span>
      </header>
      <div className="lc-panel__body">
        {permissionManquante ? (
          <p className="cm-safenote">
            Reconnectez Instagram pour autoriser la lecture des commentaires —
            bouton « Réautoriser » sur la page Intégrations → Instagram.
          </p>
        ) : null}
        {erreur ? <p className="lc-hint">{erreur}</p> : null}

        {data && data.syncedTotal >= 8 ? (
          <div className="igc-recherche">
            <Search size={14} aria-hidden="true" />
            <input className="lc-input" value={recherche} placeholder="Rechercher dans les commentaires…"
              onChange={(e) => setRecherche(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setRechercheActive(recherche.trim()) }}
              aria-label="Rechercher dans les commentaires" />
          </div>
        ) : null}

        {data && threads.length === 0 && !erreur ? (
          <p className="lc-hint">
            {rechercheActive ? 'Aucun commentaire ne correspond.'
              : permissionManquante ? null
              : 'Aucun commentaire synchronisé pour ce contenu.'}
          </p>
        ) : null}

        <div className="igc-liste lc-stagger">
          {threads.map((t) => (
            <div key={t.comment.id} className="igc-thread lc-in">
              <Commentaire c={t.comment} />
              {t.replies.length > 0 && !deploye.has(t.comment.id) && t.replies.length > 2 ? (
                <button type="button" className="lc-btn lc-btn--ghost igc-plus"
                  onClick={() => setDeploye((s) => new Set(s).add(t.comment.id))}>
                  <MessageCircle size={13} aria-hidden="true" /> Afficher {t.replies.length} réponses
                </button>
              ) : (
                t.replies.map((r) => <Commentaire key={r.id} c={r} reponse />)
              )}
            </div>
          ))}
        </div>

        {data && threads.length < data.total ? (
          <button type="button" className="lc-btn mg-plus" onClick={() => setPage((p) => p + 1)}>
            Afficher plus ({threads.length} / {data.total})
          </button>
        ) : null}
      </div>
    </section>
  )
}
